import { onboardingProductConfig, onboardingStepConfig } from './OnboardingProductConfig';
import {
  type OnboardingProduct,
  type OnboardingProducts,
  type OnboardingStep,
  OnboardingStepStatus,
  OnboardingStepVisibilityBehavior,
  type OnboardingSteps,
} from './types';

import type { Company } from 'common/api/endpoints/companies';
import type { Onboarding } from 'common/api/endpoints/onboarding';

class OnboardingProductStepsManager {
  getProductCompletionStats(
    productId: OnboardingProducts,
    company: Company,
    onboarding: Onboarding,
  ): {
    totalSteps: number;
    completedSteps: number;
    completionPercentage: number;
  } {
    const { visibleSteps } = this.getProductSteps(productId, company, onboarding);
    const completedSteps = visibleSteps.filter((step) =>
      step.isCompleted(company, onboarding),
    ).length;
    const totalSteps = visibleSteps.length;
    const completionPercentage =
      totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

    return {
      totalSteps,
      completedSteps,
      completionPercentage,
    };
  }

  getProducts(
    company: Company,
    onboarding: Onboarding,
  ): {
    visibleProducts: OnboardingProduct[];
    hiddenProducts: OnboardingProduct[];
  } {
    const visibleProducts: OnboardingProduct[] = [];
    const hiddenProducts: OnboardingProduct[] = [];

    Object.values(onboardingProductConfig)
      .filter((product) => !product.isDisabled?.(company, onboarding))
      .forEach((product) => {
        if (this.evaluateProduct(product, company, onboarding)) {
          visibleProducts.push(product);
        } else {
          hiddenProducts.push(product);
        }
      });

    return { visibleProducts, hiddenProducts };
  }

  hasUnCompletedSteps(company: Company, onboarding: Onboarding): boolean {
    const products = this.getProducts(company, onboarding);
    return products.visibleProducts.some((product) => {
      const { visibleSteps } = this.getProductSteps(product.id, company, onboarding);
      return visibleSteps.some((step) => !step.isCompleted(company, onboarding));
    });
  }

  getProductSteps(
    productId: OnboardingProducts,
    company: Company,
    onboarding: Onboarding,
  ): {
    visibleSteps: OnboardingStep[];
    overflowSteps: OnboardingStep[];
    focusedStepId: OnboardingSteps | null;
  } {
    const product = onboardingProductConfig[productId];
    if (!product) {
      return { visibleSteps: [], overflowSteps: [], focusedStepId: null };
    }

    const visibleSteps: OnboardingStep[] = [];
    const overflowSteps: OnboardingStep[] = [];
    let focusedStepId: OnboardingSteps | null = null;

    product.steps.forEach((step) => {
      const fullStep = onboardingStepConfig[step.id];

      if (fullStep.isDisabled?.(company, onboarding)) {
        return;
      }

      const isCompleted = fullStep.isCompleted(company, onboarding);
      const status = isCompleted ? OnboardingStepStatus.completed : OnboardingStepStatus.incomplete;
      fullStep.status = status;

      const isStepValid = this.evaluateStep(fullStep, company, onboarding);

      if (
        isStepValid &&
        !isCompleted &&
        !focusedStepId &&
        step.visibilityBehavior !== OnboardingStepVisibilityBehavior.overflow
      ) {
        focusedStepId = fullStep.id;
      }

      switch (step.visibilityBehavior) {
        case OnboardingStepVisibilityBehavior.always:
          visibleSteps.push(fullStep);
          break;
        case OnboardingStepVisibilityBehavior.overflow:
          overflowSteps.push({
            ...fullStep,
            title: fullStep.overflowTitle ?? fullStep.title,
            description: fullStep.overflowDescription ?? fullStep.description,
          });
          break;
        case OnboardingStepVisibilityBehavior.conditional:
          if (isStepValid) {
            visibleSteps.push(fullStep);
          } else {
            overflowSteps.push({
              ...fullStep,
              title: fullStep.overflowTitle ?? fullStep.title,
              description: fullStep.overflowDescription ?? fullStep.description,
            });
          }
          break;
      }
    });

    return { visibleSteps, overflowSteps, focusedStepId };
  }

  evaluateStep(step: OnboardingStep, company: Company, onboarding: Onboarding): boolean {
    if (!step.condition) {
      return true;
    }
    return step.condition(company, onboarding);
  }

  evaluateProduct(product: OnboardingProduct, company: Company, onboarding: Onboarding): boolean {
    if (!product.condition) {
      return true;
    }
    return product.condition(company, onboarding);
  }
}

export const onboardingManager = new OnboardingProductStepsManager();
